import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Sparkles, CheckCircle2, Medal, ShieldCheck, Leaf, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Leaderboard() {
  const [users, setUsers] = useState([]);
  const [sort, setSort] = useState("points");

  useEffect(() => { api.get("/leaderboard", { params: { sort } }).then((r) => setUsers(r.data)).catch(() => {}); }, [sort]);

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mono text-xs text-primary">// rewards · leaderboard</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Top contributors.</h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Points reward every step of the loop. Impact tracks real-world outcomes — kg of CO₂ saved and units deployed via Verified Success.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-4">
        <Rule icon={Sparkles} label="Vote received" pts="+1" />
        <Rule icon={CheckCircle2} label="Proposal filtered" pts="+25" />
        <Rule icon={Medal} label="Response accepted" pts="+100" />
        <Rule icon={ShieldCheck} label="Verified success" pts="+100" />
      </div>

      <Tabs value={sort} onValueChange={setSort} className="mt-10">
        <TabsList className="rounded-none border border-border bg-transparent p-0">
          <TabsTrigger value="points" data-testid="lb-tab-points" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Trophy className="mr-2 h-3 w-3" />By points</TabsTrigger>
          <TabsTrigger value="impact" data-testid="lb-tab-impact" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"><Leaf className="mr-2 h-3 w-3" />By impact</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6 border border-border">
        {users.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No public contributors yet.</div>
        ) : users.map((u, i) => (
          <div key={u.id} data-testid={`lb-row-${i}`} className={`flex items-center gap-4 p-5 ${i > 0 ? "border-t border-border" : ""} ${i < 3 ? "bg-primary/5" : ""}`}>
            <div className={`mono w-10 text-center text-xl font-black ${i === 0 ? "text-primary" : "text-muted-foreground"}`}>#{i + 1}</div>
            <div className="flex h-10 w-10 items-center justify-center bg-primary/20 text-sm font-bold text-primary">
              {u.display_name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 font-bold tracking-tight">
                {u.display_name}
                {u.qualified && <Badge className="bg-primary text-primary-foreground rounded-none text-[9px] tracking-widest">VERIFIED</Badge>}
              </div>
              <div className="text-xs text-muted-foreground uppercase tracking-widest">{u.role} · {u.filtered_count} filtered · {u.implemented_count ?? 0} implemented · {u.accepted_count} accepted</div>
            </div>
            {sort === "impact" ? (
              <div className="text-right flex items-center gap-4">
                <div>
                  <div className="mono flex items-center gap-1 text-lg text-primary"><Leaf className="h-4 w-4" />{u.impact_co2_kg ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">kg CO₂</div>
                </div>
                <div>
                  <div className="mono flex items-center gap-1 text-lg text-primary"><Zap className="h-4 w-4" />{u.impact_units ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">units</div>
                </div>
              </div>
            ) : (
              <div className="text-right">
                <div className="mono flex items-center gap-1 text-xl text-primary"><Trophy className="h-4 w-4" />{u.points ?? 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">points</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Link to="/companies-wall" data-testid="link-companies-wall" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary hover:underline">See the company verify wall →</Link>
      </div>
    </div>
  );
}

const Rule = ({ icon: Icon, label, pts }) => (
  <div className="border border-border p-4 flex items-center gap-3">
    <Icon className="h-5 w-5 text-primary" />
    <div className="flex-1">
      <div className="text-sm font-bold">{label}</div>
      <div className="text-xs text-muted-foreground">reward</div>
    </div>
    <div className="mono text-primary font-bold">{pts}</div>
  </div>
);
